import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Button } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'

import { PRIMARY_CTA } from '@/constants/site'

interface CtaSectionProps {
    label?: string
    title?: ReactNode
    description?: string
    secondary?: { label: string, href: string }
}

export const CtaSection: FC<CtaSectionProps> = ({
    label = 'Start a project',
    title = (
        <>
            Ready to give your business a <span className='serif-accent'>cortex?</span>
        </>
    ),
    description = 'Book a 45-minute consultation. We will map your highest-value automation opportunities and show you what a first system could look like, at no cost.',
    secondary = { label: 'See case studies', href: '/case-studies' }
}): ReactNode => {
    return (
        <section id='cta' aria-labelledby='cta-title' className='relative overflow-hidden border-t border-line'>
            <div className='absolute inset-0 overflow-hidden'>
                <div data-parallax='8' className='absolute -inset-y-[12%] inset-x-0'>
                    <Image
                        src='/images/desert-house.jpg'
                        alt=''
                        fill
                        sizes='100vw'
                        className='img-tone object-cover object-[50%_60%]'
                    />
                </div>

                <div className='absolute inset-0 bg-background/70' />
                <div className='absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/80' />
                <div className='absolute inset-0 bg-gradient-to-r from-background/90 via-background/40 to-transparent' />
            </div>

            <Container className='relative z-[2] flex min-h-[78svh] flex-col justify-end gap-12 py-24 md:py-32'>
                <SectionLabel>{label}</SectionLabel>

                <div className='grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-6'>
                    <h2 id='cta-title' data-split className='type-display max-w-[13ch] lg:col-span-8'>
                        {title}
                    </h2>

                    <div data-reveal data-delay='0.2' className='flex flex-col gap-8 lg:col-span-4'>
                        <p className='type-lead text-soft'>
                            {description}
                        </p>

                        <div className='flex flex-wrap gap-3'>
                            <Button href={PRIMARY_CTA.href}>
                                {PRIMARY_CTA.label}
                            </Button>

                            <Button href={secondary.href} variant='secondary'>
                                {secondary.label}
                            </Button>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}
